import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowLeft, ArrowRight, GitCompare, CheckCircle2, X, Clock, 
  BookOpen, Briefcase, Target, GraduationCap
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { getProgramsByCategory, PROGRAM_CATEGORIES } from "@/data/programCatalog";

/**
 * ComparePrograms - Side-by-side program comparison page
 * Users can select EMP programs and compare them across key attributes
 */
export default function ComparePrograms() {
  const [selectedPrograms, setSelectedPrograms] = useState([]);
  
  // Get EMP programs for comparison
  const empPrograms = getProgramsByCategory("executive-professional-masters");

  const toggleProgram = (programId) => {
    if (selectedPrograms.includes(programId)) {
      setSelectedPrograms(selectedPrograms.filter(id => id !== programId));
    } else if (selectedPrograms.length < 4) {
      setSelectedPrograms([...selectedPrograms, programId]);
    }
  };

  const selectedProgramData = useMemo(() => {
    return empPrograms.filter(p => selectedPrograms.includes(p.id));
  }, [selectedPrograms, empPrograms]);

  const clearSelection = () => setSelectedPrograms([]);

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="compare-programs-page">
      {/* Hero */}
      <section className="bg-white border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link 
              to="/programs" 
              className="inline-flex items-center gap-2 text-[#1E3A8A]/70 hover:text-[#1E3A8A] mb-4 text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Programs
            </Link>
            <div className="flex items-center gap-3 mb-4">
              <GitCompare className="w-8 h-8 text-[#1E3A8A]" />
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-4" style={{ color: '#991B1B' }}>
              Compare Executive Programs
            </h1>
            <p className="text-lg text-[#1E3A8A] max-w-2xl">
              Select up to 4 Executive Professional Masters programs to compare side-by-side.
            </p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Program Selector */}
          <div className="lg:col-span-1">
            <Card className="border-[#E5E7EB] bg-white sticky top-4">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg" style={{ color: '#991B1B' }}>
                  Select Programs
                </CardTitle>
                <p className="text-xs text-[#1E3A8A]/60">
                  {selectedPrograms.length}/4 selected
                </p>
              </CardHeader>
              <CardContent className="space-y-3">
                {empPrograms.map((program) => (
                  <div 
                    key={program.id}
                    className="flex items-start gap-3 p-3 rounded-lg border border-[#E5E7EB] hover:bg-[#F8FAFC] cursor-pointer"
                    onClick={() => toggleProgram(program.id)}
                  >
                    <Checkbox 
                      checked={selectedPrograms.includes(program.id)}
                      onCheckedChange={() => toggleProgram(program.id)}
                      disabled={!selectedPrograms.includes(program.id) && selectedPrograms.length >= 4}
                    />
                    <span className="text-sm text-[#1E3A8A] leading-tight">
                      {program.title}
                    </span>
                  </div>
                ))}
                
                {selectedPrograms.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full mt-4 border-[#E5E7EB]"
                    onClick={clearSelection}
                  >
                    Clear Selection
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Comparison Table */}
          <div className="lg:col-span-3">
            {selectedProgramData.length === 0 ? (
              <Card className="border-[#E5E7EB] bg-white">
                <CardContent className="p-12 text-center">
                  <GitCompare className="w-12 h-12 mx-auto mb-4 text-[#E5E7EB]" />
                  <h3 className="font-heading text-xl font-semibold mb-2 text-[#1E3A8A]">
                    No Programs Selected
                  </h3>
                  <p className="text-[#1E3A8A]/60">
                    Select programs from the list to compare them side-by-side.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                {/* Program Headers */}
                <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${selectedProgramData.length}, 1fr)` }}>
                  {selectedProgramData.map((program) => (
                    <Card key={program.id} className="border-[#E5E7EB] bg-white">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-2">
                          <GraduationCap className="w-6 h-6 text-[#1E3A8A]" />
                          <button 
                            onClick={() => toggleProgram(program.id)}
                            className="text-[#1E3A8A]/40 hover:text-[#991B1B]"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <h3 className="font-heading font-semibold text-sm leading-tight mb-2" style={{ color: '#991B1B' }}>
                          {program.title}
                        </h3>
                        <Link to={`/programs/${program.slug}`}>
                          <Button size="sm" variant="outline" className="w-full text-xs border-[#E5E7EB]">
                            View Details
                          </Button>
                        </Link>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Comparison Rows */}
                <ComparisonSection 
                  title="Program Duration" 
                  icon={Clock}
                  programs={selectedProgramData}
                  renderValue={(p) => p.duration}
                />
                
                <ComparisonSection 
                  title="Program Orientation" 
                  icon={Target}
                  programs={selectedProgramData}
                  renderValue={(p) => (
                    <div className="flex flex-wrap gap-1">
                      {p.orientation?.map((o, i) => (
                        <Badge key={i} variant="outline" className="text-xs bg-[#1E3A8A]/5 text-[#1E3A8A] border-[#1E3A8A]/20">
                          {o}
                        </Badge>
                      ))}
                    </div>
                  )}
                />
                
                <ComparisonSection 
                  title="Curriculum Modules" 
                  icon={BookOpen}
                  programs={selectedProgramData}
                  renderValue={(p) => (
                    <div className="space-y-1">
                      {p.curriculum?.map((m, i) => (
                        <div key={i} className="text-xs text-[#1E3A8A] flex items-start gap-2">
                          <CheckCircle2 className="w-3 h-3 text-[#1E3A8A] mt-0.5 flex-shrink-0" />
                          <span>{m.title}</span>
                        </div>
                      ))}
                    </div>
                  )}
                />
                
                <ComparisonSection 
                  title="Career Pathways" 
                  icon={Briefcase}
                  programs={selectedProgramData}
                  renderValue={(p) => (
                    <div className="space-y-1">
                      {p.careerPathways?.slice(0, 4).map((c, i) => (
                        <div key={i} className="text-xs text-[#1E3A8A] flex items-start gap-2">
                          <ArrowRight className="w-3 h-3 text-[#991B1B] mt-0.5 flex-shrink-0" />
                          <span>{c}</span>
                        </div>
                      ))}
                    </div>
                  )}
                />
                
                <ComparisonSection 
                  title="Learning Focus" 
                  icon={Target}
                  programs={selectedProgramData}
                  renderValue={(p) => (
                    <p className="text-xs text-[#1E3A8A] leading-relaxed">
                      {p.shortDescription}
                    </p>
                  )}
                />

                {/* Apply CTA */}
                <Card className="border-[#E5E7EB] bg-gradient-to-br from-[#1E3A8A] to-[#2563EB] text-white">
                  <CardContent className="p-6 text-center">
                    <h3 className="font-heading text-xl font-semibold mb-2">
                      Ready to Apply?
                    </h3>
                    <p className="text-white/80 text-sm mb-4">
                      Choose your program and start your application today.
                    </p>
                    <Link to="/programs/apply">
                      <Button className="bg-white text-[#1E3A8A] hover:bg-white/90">
                        Apply Now
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ComparisonSection({ title, icon: Icon, programs, renderValue }) {
  return (
    <Card className="border-[#E5E7EB] bg-white">
      <CardHeader className="pb-2 border-b border-[#E5E7EB]">
        <CardTitle className="text-sm font-semibold flex items-center gap-2" style={{ color: '#991B1B' }}>
          <Icon className="w-4 h-4" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="grid divide-x divide-[#E5E7EB]" style={{ gridTemplateColumns: `repeat(${programs.length}, 1fr)` }}>
          {programs.map((program) => (
            <div key={program.id} className="p-4">
              {renderValue(program)}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
